import React, {
  type ForwardedRef,
  forwardRef,
  memo,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Box,
  Button,
  ButtonCore,
  ButtonIcon,
  Icon,
  Loader,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { CF_CRT_MED, CF_NEU_BLD, CF_SEC_MED } from '../../constants';
import { useInvertedLocalize } from '../../hooks';
import { type InsightItemTypes } from '../../types';

import './SodInsights.css';

interface SodInsightsProps {
  data: InsightItemTypes[] | null;
  isLoading?: boolean;
  isError?: boolean;
  activeInsight: InsightItemTypes | null;
  handleRefresh: () => void;
  handleOptionClick: (insightItem: InsightItemTypes | null) => void;
  isInsightsOpen: boolean;
  setIsInsightsOpen: (isOpen: boolean) => void;
  footerContent?: ReactNode;
  className?: string;
}

export interface SodInsightsHandle {
  home: () => void;
}

/**
 * Needs 2 kinds:
 * - List Style: MVP and basic (Done)
 * - Sidepanel Style: Can we place the DS sidepanel inside and have it done automatically?
 *
 * * ==== SodInsights TODO List ====
 * - Sidepanel kind
 * - Replace mockInsightItems with real data
 * - Localize these labels
 */
const SodInsights = forwardRef(function SodInsights(
  {
    data,
    isLoading,
    isError,
    activeInsight,
    handleRefresh,
    handleOptionClick,
    isInsightsOpen,
    setIsInsightsOpen,
    footerContent,
    className,
  }: SodInsightsProps,
  ref: ForwardedRef<SodInsightsHandle>
) {
  const l = useInvertedLocalize();
  const [selectedInsight, setSelectedInsight] =
    useState<InsightItemTypes | null>(null);

  const insightsWrapperClasses = cx(
    'SodInsights-wrapper',
    isInsightsOpen && 'SodInsights-wrapper--isInsightsOpen',
    className
  );

  useEffect(() => {
    if (activeInsight) {
      setSelectedInsight(activeInsight);
    }
  }, [activeInsight]);

  const handleInsightClose = () => {
    setIsInsightsOpen(false);
    setSelectedInsight(null);
    handleOptionClick(null);
  };

  useImperativeHandle(ref, () => ({
    home: () => {
      setSelectedInsight(null);
      handleOptionClick(null);
    },
  }));

  const renderBody = () => {
    if (isError || !data) {
      return (
        <Box className='SodInsights'>
          <Box className='SodInsights-listHeader'>
            <Box className='SodInsights-listHeaderTextWrapper'>
              <Icon kind='SaviCompanion' size='medium' color={CF_NEU_BLD} />
              <Typography kind='h2' color={CF_NEU_BLD}>
                {l('Insights')}
              </Typography>
            </Box>
            <ButtonIcon
              kind='ghost'
              size='medium'
              icon={<Icon kind='Close' />}
              onClick={handleInsightClose}
              ariaLabel='Close Risk SodInsights Aside'
            />
          </Box>
          {isLoading ? (
            <Box tag='div' className='SodInsights-loadingWrapper'>
              <Loader kind='dots' format='inline' color='brand' />
            </Box>
          ) : (
            <Box tag='div' className='SodInsights-loadingWrapper'>
              <Box tag='div' className='SodInsights-loadingWrapper-message'>
                <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
                <Typography kind='body2' color={CF_CRT_MED}>
                  {l("Data couldn't be retrieved.")}
                </Typography>
              </Box>
              <Button
                kind='filled'
                size='small'
                onClick={() => handleRefresh()}>
                {l('Refresh')}
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    if (Array.isArray(data) && data.length === 0) {
      return (
        <Box className='SodInsights'>
          <Box className='SodInsights-listHeader'>
            <Box className='SodInsights-listHeaderTextWrapper'>
              <Icon kind='SaviCompanion' size='medium' color={CF_NEU_BLD} />
              <Typography kind='h2' color={CF_NEU_BLD}>
                {l('Insights')}
              </Typography>
            </Box>
            <ButtonIcon
              kind='ghost'
              size='medium'
              icon={<Icon kind='Close' />}
              onClick={handleInsightClose}
              ariaLabel='Close Risk SodInsights Aside'
            />
          </Box>
          <Box tag='div' className='SodInsights-loadingWrapper'>
            <Box tag='div' className='SodInsights-loadingWrapper-message'>
              <Icon kind='AlertInfo' size='large' color={CF_NEU_BLD} />
              <Typography kind='body2'>{l('No Insights Available')}</Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box className='SodInsights'>
        <Box
          className={
            selectedInsight
              ? 'SodInsights-listHeader-subTab'
              : 'SodInsights-listHeader'
          }>
          <Box className='SodInsights-listHeaderTextWrapper'>
            {selectedInsight && (
              <ButtonIcon
                kind='ghost'
                size='medium'
                icon={<Icon kind='ChevronLeft' />}
                onClick={() => {
                  setSelectedInsight(null);
                  handleOptionClick(null);
                }}
                ariaLabel='Back to Risk SodInsights List'
              />
            )}
            <Icon kind='SaviCompanion' size='medium' color={CF_NEU_BLD} />
            <Typography
              kind={selectedInsight ? 'body2' : 'h2'}
              color={CF_NEU_BLD}>
              {l(selectedInsight ? 'Insight Applied' : 'Insights')}
            </Typography>
          </Box>
          <ButtonIcon
            kind='ghost'
            size='medium'
            icon={<Icon kind='Close' />}
            onClick={handleInsightClose}
            ariaLabel='Close Risk SodInsights Aside'
          />
        </Box>
        {!selectedInsight && (
          <ul className='SodInsights-list'>
            {data.map((item) => (
              <li key={item.id} className='SodInsights-listItem'>
                <Icon kind='SaviCompanion' size='medium' color={CF_SEC_MED} />
                <ButtonCore
                  onClick={() => {
                    setSelectedInsight({
                      ...item,
                      details: {
                        timestamp: new Date().toISOString(),
                        source: 'violation-tab-user-insights',
                      },
                    });
                    handleOptionClick({
                      ...item,
                      details: {
                        timestamp: new Date().toISOString(),
                        source: 'violation-tab-user-insights',
                      },
                    });
                  }}
                  className='SodInsights-listItemButton'>
                  <Typography kind='body2' color={CF_NEU_BLD}>
                    {item.title}
                  </Typography>
                </ButtonCore>
                <Icon
                  kind='ArrowRight'
                  size='medium'
                  color={CF_NEU_BLD}
                  className='SodInsights-listItemButton-arrowRightIcon'
                />
              </li>
            ))}
          </ul>
        )}
        {selectedInsight && (
          <Box className='SodInsights-listItemDetails'>
            <Typography
              kind='h3'
              color={CF_NEU_BLD}
              className='SodInsights-listDetailsHeader'>
              {selectedInsight.title}
            </Typography>
            <Typography
              kind='body2'
              color={CF_NEU_BLD}
              className='SodInsights-listDetailsText'>
              {selectedInsight.description}
            </Typography>
          </Box>
        )}
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
        {false && selectedInsight && footerContent}
      </Box>
    );
  };

  return (
    <aside aria-hidden={!isInsightsOpen} className={insightsWrapperClasses}>
      {renderBody()}
    </aside>
  );
});

SodInsights.defaultProps = {
  isLoading: true,
  isError: false,
  footerContent: null,
  className: undefined,
};

export default memo(SodInsights);
